import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useQuestionBook } from 'src/app/hooks/useQuestionBook';
import { useTranslation } from 'react-i18next';
import { FormCheck, FormField } from '@/components/custom/form';
import { Button } from '@/components/ui/button';
import { FormTaxonomy } from '@/components/custom/form';

export function ExercisesForm(props) {
  const { t } = useTranslation();
  const { setShowModal } = props;
  const client = useClient();
  const { createQuestionBook } = useQuestionBook();
  const [taxonomies, setTaxonomies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModalTaxonomies, setIsOpenModalTaxonomies] = useState(false);

  useEffect(() => {
    fetchTaxonomies();
  }, []);

  const fetchTaxonomies = async () => {
    try {
      const { data } = await client.fetch('taxonomies');

      setTaxonomies(data);
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      toast.error(t('toast.errorGetTags'));
    }
  };

  const schema = Yup.object({
    title: Yup.string().required(),
    interactionTypes: Yup.array().min(1).required(),
    selectedTaxonomies: Yup.array().min(1).required()
  });

  const form = useFormik({
    initialValues: {
      title: '',
      interactionTypes: [],
      selectedTaxonomies: []
    },
    validationSchema: schema,
    onSubmit: values => {
      const taxonomyIds = values.selectedTaxonomies.map(t => t.id);

      createQuestionBook({
        ...values,
        selectedTaxonomies: undefined,
        taxonomyIds: taxonomyIds,
        published: true
      });
      setShowModal(false);
    }
  });

  /**
   * Função auxiliar para criar um "evento" sintético que o Formik espera,
   * permitindo o uso do form.handleChange com componentes customizados (FormCheck).
   * @param {boolean} checked - O estado booleano do checkbox.
   * @param {string} name - O nome do campo Formik ('interactionTypes').
   * @param {string} value - O valor do checkbox ('neverAnswered', 'neverGot', etc.).
   */
  const createSyntheticChangeEvent = (checked, name, value) => {
    // Cria o objeto 'target' com as propriedades que o Formik espera.
    return {
      target: {
        name: name,
        value: value,
        type: 'checkbox',
        checked: checked
      }
    };
  };

  return (
    <form
      method="post"
      className="space-y-5"
      onSubmit={form.submitForm}
    >
      <FormField
        label={t('exams.questionBookName')}
        id="notebookName"
        name="notebookName"
        type="text"
        value={form.values.title}
        onChange={e => form.setFieldValue('title', e.target.value)}
        hasError={form.errors.title && form.touched.title}
        errorText={form.errors.title}
      />

      <FormTaxonomy
        variant="tags"
        isLoading={isLoading}
        taxonomies={taxonomies}
        selectedTaxonomies={form.values.selectedTaxonomies}
        setFieldValue={form.setFieldValue}
        errors={form.errors}
        touched={form.touched}
        isOpenModalTaxonomies={isOpenModalTaxonomies}
        setIsOpenModalTaxonomies={setIsOpenModalTaxonomies}
      />

      <FormField
        label={t('questionBooks.labelMyInteractions')}
        hasError={form.errors.interactionTypes && form.touched.interactionTypes}
        errorText={form.errors.interactionTypes}
      >
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <FormCheck
            label={t('questionBooks.labelNeverAnswered')}
            name="interactionTypes"
            value="neverAnswered"
            id="neverAnswered"
            onCheckedChange={checked => {
              const syntheticEvent = createSyntheticChangeEvent(checked, 'interactionTypes', 'neverAnswered');
              form.handleChange(syntheticEvent);
            }}
          />

          <FormCheck
            label={t('questionBooks.labelNeverGot')}
            name="interactionTypes"
            value="neverGot"
            id="neverGot"
            onCheckedChange={checked => {
              const syntheticEvent = createSyntheticChangeEvent(checked, 'interactionTypes', 'neverGot');
              form.handleChange(syntheticEvent);
            }}
          />

          <FormCheck
            label={t('questionBooks.labelGotAndWrong')}
            name="interactionTypes"
            value="gotAndWrong"
            id="gotAndWrong"
            onCheckedChange={checked => {
              const syntheticEvent = createSyntheticChangeEvent(checked, 'interactionTypes', 'gotAndWrong');
              form.handleChange(syntheticEvent);
            }}
          />
        </div>
      </FormField>

      <Button
        type="button"
        fullWidth
        onClick={form.submitForm}
      >
        Criar exercício
      </Button>
    </form>
  );
}
